import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  CircleDot,
  Clock3,
  Copy,
  Download,
  FileText,
  Film,
  Headphones,
  Info,
  Loader2,
  Mic,
  NotebookPen,
  Pause,
  Play,
  RefreshCw,
  ScreenShare,
  Search,
  Square,
  Trash2,
  Users,
  Video,
  X,
} from 'lucide-react';
import {
  recallApi,
  type RecallRecording,
  type RecallTranscriptResponse,
  type RecallTranscriptSegment,
} from '@/services/recall.api';
import { getApiErrorMessage } from '@/lib/http';
import { useAuth } from '@/auth/AuthProvider';
import { SidebarExpandButton } from '@/components/SidebarExpandButton';

type Compatibility = {
  level: 'supported' | 'unsupported' | 'desktop-required' | 'unknown';
  headline: string;
  detail: string;
};

type SessionStatus = 'detected' | 'recording' | 'paused' | 'ended';

type LiveSession = {
  windowId: string;
  title?: string;
  url?: string;
  platform?: string;
  detectedAt: number;
  status: SessionStatus;
  recordingId?: string;
  uploadId?: string;
  liveTranscript: LiveTranscriptLine[];
};

type LiveTranscriptLine = {
  id: string;
  speaker: string;
  text: string;
  ts: number;
};

type LogEntry = {
  id: string;
  ts: number;
  level: 'info' | 'warn' | 'error';
  message: string;
};

type DetailTab = 'transcript' | 'video' | 'audio' | 'meta';

const MAX_LOG_ENTRIES = 200;
const MAX_LIVE_LINES = 400;
const RECORDINGS_LIMIT = 30;

const PLATFORM_LABEL: Record<string, string> = {
  zoom: 'Zoom',
  meet: 'Google Meet',
  google_meet: 'Google Meet',
  googlemeet: 'Google Meet',
  msteams: 'Microsoft Teams',
  ms_teams: 'Microsoft Teams',
  teams: 'Microsoft Teams',
  slack: 'Slack',
  adhoc: 'Ad-hoc',
};

function platformLabel(platform?: string | null) {
  if (!platform) return 'Unknown';
  const key = platform.toLowerCase();
  return PLATFORM_LABEL[key] || platform;
}

function detectCompatibility(): Compatibility {
  const desktop = typeof window !== 'undefined' ? window.lanternDesktop : undefined;

  if (!desktop) {
    return {
      level: 'desktop-required',
      headline: 'Open Lantern desktop to record',
      detail:
        'Recording happens locally on your Mac or PC. You can browse and read past notetaker sessions here, but starting a new one requires the Lantern desktop app.',
    };
  }

  if (desktop.platform === 'linux') {
    return {
      level: 'unsupported',
      headline: 'Linux not supported',
      detail: 'Recall.ai supports macOS (Apple Silicon) and Windows. Past sessions are still readable here.',
    };
  }

  return {
    level: 'supported',
    headline: 'Notetaker is on standby',
    detail:
      desktop.platform === 'darwin'
        ? 'Open a Zoom, Google Meet, Microsoft Teams, or Slack call and we will detect it automatically.'
        : 'Open a Zoom, Meet, Teams, or Slack call and we will detect it automatically.',
  };
}

function formatDuration(start?: string | null, end?: string | null) {
  if (!start) return '';
  const startMs = Date.parse(start);
  const endMs = end ? Date.parse(end) : Date.now();
  if (Number.isNaN(startMs) || Number.isNaN(endMs)) return '';
  const seconds = Math.max(0, Math.round((endMs - startMs) / 1000));
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function formatTimestamp(seconds?: number) {
  if (typeof seconds !== 'number' || Number.isNaN(seconds)) return '';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function shortDateTime(value?: string | null) {
  if (!value) return '—';
  const ms = Date.parse(value);
  if (Number.isNaN(ms)) return value;
  const d = new Date(ms);
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

type RecordingGroup = { label: string; recordings: RecallRecording[] };

function groupRecordings(recordings: RecallRecording[]): RecordingGroup[] {
  const today: RecallRecording[] = [];
  const yesterday: RecallRecording[] = [];
  const thisWeek: RecallRecording[] = [];
  const older: RecallRecording[] = [];

  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const startOfYesterday = new Date(startOfToday);
  startOfYesterday.setDate(startOfYesterday.getDate() - 1);
  const startOfWeek = new Date(startOfToday);
  startOfWeek.setDate(startOfWeek.getDate() - 6);

  for (const rec of recordings) {
    const created = rec.created_at ? Date.parse(rec.created_at) : 0;
    if (created >= startOfToday.getTime()) today.push(rec);
    else if (created >= startOfYesterday.getTime()) yesterday.push(rec);
    else if (created >= startOfWeek.getTime()) thisWeek.push(rec);
    else older.push(rec);
  }

  return [
    { label: 'Today', recordings: today },
    { label: 'Yesterday', recordings: yesterday },
    { label: 'This week', recordings: thisWeek },
    { label: 'Older', recordings: older },
  ].filter((g) => g.recordings.length > 0);
}

function recordingTitle(rec: RecallRecording) {
  const meta = (rec.metadata || {}) as Record<string, unknown>;
  const sdkMeta = (rec.sdk_upload?.metadata || {}) as Record<string, unknown>;
  return (
    (sdkMeta.title as string | undefined) ||
    (meta.title as string | undefined) ||
    (sdkMeta.windowTitle as string | undefined) ||
    `Recording ${rec.id.slice(0, 8)}`
  );
}

function recordingPlatform(rec: RecallRecording): string | undefined {
  const sdkMeta = (rec.sdk_upload?.metadata || {}) as Record<string, unknown>;
  return (
    (sdkMeta.platform as string | undefined) ||
    ((rec.metadata as Record<string, unknown>)?.platform as string | undefined)
  );
}

function transcriptStatus(rec: RecallRecording): string {
  const code = rec.media_shortcuts?.transcript?.status?.code;
  if (code) return code;
  if (rec.completed_at) return 'pending';
  return rec.status?.code || 'in_progress';
}

function pillClassFor(code: string) {
  if (code === 'done') return 'nt-pill is-done';
  if (code === 'failed' || code === 'error') return 'nt-pill is-danger';
  if (code === 'processing' || code === 'pending' || code === 'in_progress') return 'nt-pill is-warn';
  return 'nt-pill';
}

const STUCK_THRESHOLD_MS = 10 * 60 * 1000;

function isStuckRecording(rec: RecallRecording, activeLiveIds: Set<string>): boolean {
  if (activeLiveIds.has(rec.id)) return false;
  if (rec.completed_at) return false;
  const status = transcriptStatus(rec);
  if (status === 'done' || status === 'failed' || status === 'error') return false;
  if (!rec.created_at) return false;
  const createdMs = Date.parse(rec.created_at);
  if (Number.isNaN(createdMs)) return false;
  return Date.now() - createdMs > STUCK_THRESHOLD_MS;
}

function shortcutUrl(rec: RecallRecording | null, key: string): string | null {
  const s = rec?.media_shortcuts?.[key];
  if (!s) return null;
  return s.data?.download_url || s.download_url || null;
}

function shortcutStatus(rec: RecallRecording | null, key: string): string | null {
  const s = rec?.media_shortcuts?.[key];
  return s?.status?.code || null;
}

function findShortcutByPrefix(rec: RecallRecording | null, prefixes: string[]): { key: string; url: string } | null {
  const shortcuts = rec?.media_shortcuts || {};
  for (const key of Object.keys(shortcuts)) {
    const lower = key.toLowerCase();
    if (!prefixes.some((p) => lower.startsWith(p))) continue;
    const url = shortcutUrl(rec, key);
    if (url) return { key, url };
  }
  return null;
}

function videoUrl(rec: RecallRecording | null) {
  return findShortcutByPrefix(rec, ['video_'])?.url || null;
}

function audioUrl(rec: RecallRecording | null) {
  return findShortcutByPrefix(rec, ['audio_'])?.url || null;
}

function videoStatusOf(rec: RecallRecording | null): string | null {
  const found = findShortcutByPrefix(rec, ['video_']);
  return found ? shortcutStatus(rec, found.key) : null;
}

function audioStatusOf(rec: RecallRecording | null): string | null {
  const found = findShortcutByPrefix(rec, ['audio_']);
  return found ? shortcutStatus(rec, found.key) : null;
}

function downloadUrl(url: string, filename: string) {
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.target = '_blank';
  a.rel = 'noopener';
  a.click();
}

function safeFilename(stem: string, ext: string) {
  const base = stem.replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '').slice(0, 80) || 'recording';
  return `${base}.${ext}`;
}

export function NotetakersPage() {
  const { hasPermission } = useAuth();
  // Members keep read-only access — they can browse recordings, transcripts,
  // and downloads, but not start/stop/delete or trigger cleanup.
  const canUseNotetakers = hasPermission('notetaker.use');
  const [compatibility, setCompatibility] = useState<Compatibility | null>(null);
  const [sdkReady, setSdkReady] = useState(false);
  const [sdkError, setSdkError] = useState<string | null>(null);
  const [sdkState, setSdkState] = useState<'idle' | 'recording' | 'paused'>('idle');
  const [liveSessions, setLiveSessions] = useState<LiveSession[]>([]);
  const [pendingStart, setPendingStart] = useState(false);

  const [recordings, setRecordings] = useState<RecallRecording[]>([]);
  const [recordingsLoading, setRecordingsLoading] = useState(false);
  const [recordingsError, setRecordingsError] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [recordingDetail, setRecordingDetail] = useState<RecallRecording | null>(null);
  const [recordingDetailLoading, setRecordingDetailLoading] = useState(false);
  const [transcript, setTranscript] = useState<RecallTranscriptResponse | null>(null);
  const [transcriptLoading, setTranscriptLoading] = useState(false);
  const [transcriptError, setTranscriptError] = useState<string | null>(null);
  const [detailTab, setDetailTab] = useState<DetailTab>('transcript');

  const [filter, setFilter] = useState('');
  const [showLogs, setShowLogs] = useState(false);
  const [log, setLog] = useState<LogEntry[]>([]);
  const logIdRef = useRef(0);
  const liveLineIdRef = useRef(0);

  const desktop = typeof window !== 'undefined' ? window.lanternDesktop : undefined;
  const recall = desktop?.recall;
  const liveTranscriptScrollRef = useRef<HTMLDivElement | null>(null);

  const pushLog = useCallback((message: string, level: LogEntry['level'] = 'info') => {
    logIdRef.current += 1;
    const entry: LogEntry = { id: `log-${logIdRef.current}`, ts: Date.now(), level, message };
    setLog((prev) => [entry, ...prev].slice(0, MAX_LOG_ENTRIES));
  }, []);

  const updateLiveSession = useCallback(
    (windowId: string, patch: Partial<LiveSession> | ((s: LiveSession) => LiveSession)) => {
      setLiveSessions((prev) =>
        prev.map((s) => {
          if (s.windowId !== windowId) return s;
          return typeof patch === 'function' ? patch(s) : { ...s, ...patch };
        }),
      );
    },
    [],
  );

  useEffect(() => {
    setCompatibility(detectCompatibility());
  }, []);

  useEffect(() => {
    if (!recall) return undefined;
    let cancelled = false;
    (async () => {
      const result = await recall.init({ acquirePermissionsOnStartup: ['microphone', 'screen-capture'] });
      if (cancelled) return;
      if (result.ok) {
        setSdkReady(true);
        pushLog(result.alreadyInitialized ? 'SDK already initialized' : 'SDK initialized');
      } else {
        setSdkError(result.error || 'unknown');
        pushLog(`SDK init failed: ${result.error || 'unknown'}`, 'error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [recall, pushLog]);

  const loadRecordings = useCallback(
    async (cursor?: string) => {
      setRecordingsLoading(true);
      setRecordingsError(null);
      try {
        const page = await recallApi.listRecordings({
          cursor,
          limit: RECORDINGS_LIMIT,
          ordering: '-created_at',
        });
        setRecordings((prev) => (cursor ? [...prev, ...page.results] : page.results));
        setNextCursor(page.next);
      } catch (err) {
        setRecordingsError(getApiErrorMessage(err, 'Failed to load recordings'));
      } finally {
        setRecordingsLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    void loadRecordings();
  }, [loadRecordings]);

  // Fetch full recording detail + transcript when a session is selected.
  useEffect(() => {
    if (!selectedId) {
      setTranscript(null);
      setRecordingDetail(null);
      return undefined;
    }
    let cancelled = false;
    setTranscript(null);
    setTranscriptLoading(true);
    setTranscriptError(null);
    setRecordingDetail(null);
    setRecordingDetailLoading(true);
    setDetailTab('transcript');

    void (async () => {
      try {
        const detail = await recallApi.getRecording(selectedId);
        if (!cancelled) setRecordingDetail(detail);
      } catch {
        // Non-fatal: list-derived recording is still rendered.
      } finally {
        if (!cancelled) setRecordingDetailLoading(false);
      }
    })();

    void (async () => {
      try {
        const result = await recallApi.getTranscript(selectedId);
        if (!cancelled) setTranscript(result);
      } catch (err) {
        if (!cancelled) setTranscriptError(getApiErrorMessage(err, 'Failed to load transcript'));
      } finally {
        if (!cancelled) setTranscriptLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  useEffect(() => {
    if (!recall) return undefined;

    const unsubscribe = recall.onEvent((event) => {
      switch (event.type) {
        case 'meeting-detected': {
          const w = event.payload.window;
          setLiveSessions((prev) => {
            if (prev.some((s) => s.windowId === w.id)) return prev;
            return [
              {
                windowId: w.id,
                title: w.title,
                url: w.url,
                platform: w.platform,
                detectedAt: Date.now(),
                status: 'detected',
                liveTranscript: [],
              },
              ...prev,
            ];
          });
          pushLog(`Meeting detected: ${w.title || w.id}`);
          break;
        }
        case 'meeting-updated':
          updateLiveSession(event.payload.window.id, {
            title: event.payload.window.title,
            url: event.payload.window.url,
          });
          break;
        case 'meeting-closed':
          updateLiveSession(event.payload.window.id, { status: 'ended' });
          break;
        case 'recording-started':
          updateLiveSession(event.payload.window.id, { status: 'recording' });
          pushLog(`Recording started: ${event.payload.window.title || event.payload.window.id}`);
          break;
        case 'recording-ended':
          updateLiveSession(event.payload.window.id, { status: 'ended' });
          pushLog('Recording ended — uploading to Recall');
          setTimeout(() => void loadRecordings(), 4_000);
          break;
        case 'sdk-state-change':
          setSdkState(event.payload.sdk.state.code);
          break;
        case 'permission-status':
          pushLog(`Permission ${event.payload.permission}: ${event.payload.status}`);
          break;
        case 'permissions-granted':
          pushLog('All required permissions granted');
          break;
        case 'error':
          pushLog(`SDK error (${event.payload.type}): ${event.payload.message}`, 'error');
          break;
        case 'realtime-event': {
          const data = event.payload.data as
            | { words?: Array<{ text?: string }>; participant?: { name?: string }; text?: string }
            | undefined;
          const realtimeName = event.payload.event;
          pushLog(`Realtime: ${realtimeName}`);
          if (realtimeName === 'transcript.data' || realtimeName === 'transcript.partial_data') {
            const speaker = data?.participant?.name || 'Speaker';
            const text =
              data?.text ||
              (Array.isArray(data?.words) ? data!.words!.map((w) => w.text || '').join(' ').trim() : '');
            if (text) {
              liveLineIdRef.current += 1;
              const line: LiveTranscriptLine = {
                id: `line-${liveLineIdRef.current}`,
                speaker,
                text,
                ts: Date.now(),
              };
              updateLiveSession(event.payload.window.id, (s) => ({
                ...s,
                liveTranscript: [...s.liveTranscript, line].slice(-MAX_LIVE_LINES),
              }));
              if (liveTranscriptScrollRef.current) {
                liveTranscriptScrollRef.current.scrollTop = liveTranscriptScrollRef.current.scrollHeight;
              }
            }
          }
          break;
        }
        default:
          break;
      }
    });

    return () => unsubscribe();
  }, [recall, pushLog, updateLiveSession, loadRecordings]);

  const startRecording = useCallback(
    async (windowId: string, title?: string, platform?: string) => {
      if (!recall) return;
      setPendingStart(true);
      try {
        const upload = await recallApi.createSdkUpload({
          ...(title ? { title } : {}),
          ...(platform ? { platform } : {}),
          startedAt: new Date().toISOString(),
        });
        updateLiveSession(windowId, { recordingId: upload.recording_id, uploadId: upload.id });
        const result = await recall.startRecording({ windowId, uploadToken: upload.upload_token });
        if (!result.ok) pushLog(`startRecording failed: ${result.error}`, 'error');
      } catch (err) {
        pushLog(getApiErrorMessage(err, 'Failed to mint upload token'), 'error');
      } finally {
        setPendingStart(false);
      }
    },
    [recall, pushLog, updateLiveSession],
  );

  const stopRecording = useCallback(
    async (windowId: string) => {
      if (!recall) return;
      const result = await recall.stopRecording({ windowId });
      if (!result.ok) pushLog(`stopRecording failed: ${result.error}`, 'error');
    },
    [recall, pushLog],
  );

  const pauseRecording = useCallback(
    async (windowId: string) => {
      if (!recall) return;
      const result = await recall.pauseRecording({ windowId });
      if (!result.ok) pushLog(`pauseRecording failed: ${result.error}`, 'error');
    },
    [recall, pushLog],
  );

  const resumeRecording = useCallback(
    async (windowId: string) => {
      if (!recall) return;
      const result = await recall.resumeRecording({ windowId });
      if (!result.ok) pushLog(`resumeRecording failed: ${result.error}`, 'error');
    },
    [recall, pushLog],
  );

  const startAdhoc = useCallback(async () => {
    if (!recall) return;
    setPendingStart(true);
    try {
      const prepared = await recall.prepareDesktopAudioRecording();
      if (!prepared.ok || !prepared.windowId) {
        pushLog(`prepareDesktopAudioRecording failed: ${prepared.error || 'no windowId'}`, 'error');
        return;
      }
      const windowId = prepared.windowId;
      setLiveSessions((prev) => {
        if (prev.some((s) => s.windowId === windowId)) return prev;
        return [
          {
            windowId,
            title: 'Ad-hoc screen + audio',
            platform: 'adhoc',
            detectedAt: Date.now(),
            status: 'detected',
            liveTranscript: [],
          },
          ...prev,
        ];
      });
      pushLog(`Ad-hoc session prepared (${windowId})`);
      await startRecording(windowId, 'Ad-hoc screen + audio', 'adhoc');
    } finally {
      setPendingStart(false);
    }
  }, [recall, pushLog, startRecording]);

  const filteredRecordings = useMemo(() => {
    if (!filter.trim()) return recordings;
    const needle = filter.toLowerCase();
    return recordings.filter((rec) => recordingTitle(rec).toLowerCase().includes(needle) || rec.id.includes(needle));
  }, [recordings, filter]);
  const grouped = useMemo(() => groupRecordings(filteredRecordings), [filteredRecordings]);

  const selectedRecording = useMemo(() => {
    if (recordingDetail && recordingDetail.id === selectedId) return recordingDetail;
    return recordings.find((r) => r.id === selectedId) || null;
  }, [recordings, selectedId, recordingDetail]);

  const activeLiveSession = useMemo(
    () => liveSessions.find((s) => s.status === 'recording' || s.status === 'paused'),
    [liveSessions],
  );

  const stateLabel = useMemo(() => {
    if (!recall) return 'Read-only · open desktop app to record';
    if (sdkError) return `SDK error: ${sdkError}`;
    if (!sdkReady) return 'Initializing notetaker';
    if (sdkState === 'recording') return 'Recording in progress';
    if (sdkState === 'paused') return 'Recording paused';
    return 'Listening for meetings';
  }, [recall, sdkReady, sdkError, sdkState]);

  const stateDot = useMemo(() => {
    if (!recall || sdkError) return '#b3261e';
    if (!sdkReady) return '#a96a0a';
    if (sdkState === 'recording') return '#c14a3c';
    if (sdkState === 'paused') return '#a96a0a';
    return '#2e7d4f';
  }, [recall, sdkReady, sdkError, sdkState]);

  async function handleDelete(id: string) {
    try {
      await recallApi.deleteRecording(id);
      setRecordings((prev) => prev.filter((r) => r.id !== id));
      if (selectedId === id) setSelectedId(null);
      pushLog(`Deleted recording ${id.slice(0, 8)}`);
    } catch (err) {
      pushLog(getApiErrorMessage(err, 'Delete failed'), 'error');
    }
  }

  const activeLiveIds = useMemo(() => {
    const ids = new Set<string>();
    for (const s of liveSessions) {
      if (s.recordingId) ids.add(s.recordingId);
    }
    return ids;
  }, [liveSessions]);

  const stuckRecordings = useMemo(
    () => recordings.filter((r) => isStuckRecording(r, activeLiveIds)),
    [recordings, activeLiveIds],
  );

  const [cleaningUp, setCleaningUp] = useState(false);
  const [cleanupNotice, setCleanupNotice] = useState<{
    tone: 'success' | 'partial' | 'error';
    headline: string;
    detail: string;
  } | null>(null);

  async function handleCleanupStuck() {
    if (stuckRecordings.length === 0) return;
    const ok = window.confirm(
      `Try to clean up ${stuckRecordings.length} stuck session${stuckRecordings.length === 1 ? '' : 's'} on Recall?\n\n` +
        'These never received a clean stop signal. We will attempt to abort them on Recall and delete. ' +
        'If Recall is still actively processing them, the delete may be refused — they will auto-fail after Recall\'s ' +
        'server timeout and you can retry then.',
    );
    if (!ok) return;
    setCleaningUp(true);
    setCleanupNotice(null);
    const ids = stuckRecordings.map((r) => r.id);
    const results = await Promise.allSettled(ids.map((id) => recallApi.deleteRecording(id)));
    const succeeded: string[] = [];
    const failed: Array<{ id: string; message: string }> = [];
    results.forEach((res, idx) => {
      if (res.status === 'fulfilled') succeeded.push(ids[idx]);
      else failed.push({ id: ids[idx], message: getApiErrorMessage(res.reason, 'unknown') });
    });
    if (succeeded.length > 0) {
      setRecordings((prev) => prev.filter((r) => !succeeded.includes(r.id)));
      if (selectedId && succeeded.includes(selectedId)) setSelectedId(null);
      pushLog(`Cleaned up ${succeeded.length} stuck session${succeeded.length === 1 ? '' : 's'}`);
    }
    for (const f of failed) {
      pushLog(`Cleanup failed for ${f.id.slice(0, 8)}: ${f.message}`, 'error');
    }

    if (failed.length === 0) {
      setCleanupNotice({
        tone: 'success',
        headline: `Cleaned up ${succeeded.length} stuck session${succeeded.length === 1 ? '' : 's'}`,
        detail: 'Recall has been notified and the entries are removed from the list.',
      });
    } else if (succeeded.length === 0) {
      const sample = failed[0].message;
      setCleanupNotice({
        tone: 'error',
        headline: `Recall refused to delete ${failed.length} session${failed.length === 1 ? '' : 's'}`,
        detail: sample,
      });
    } else {
      setCleanupNotice({
        tone: 'partial',
        headline: `Cleaned up ${succeeded.length}, ${failed.length} refused`,
        detail: failed[0].message,
      });
    }
    setCleaningUp(false);
  }

  return (
    <section className="page-surface notetakers-page">
      <header className="page-header">
        <div className="page-title-row">
          <SidebarExpandButton style={{ marginLeft: -6 }} />
          <span className="page-icon">
            <NotebookPen size={18} />
          </span>
          <div>
            <p className="eyebrow" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span
                aria-hidden="true"
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: stateDot,
                  display: 'inline-block',
                }}
              />
              {stateLabel}
            </p>
            <h1>Notetakers</h1>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {stuckRecordings.length > 0 && canUseNotetakers ? (
            <button
              type="button"
              className="secondary-button is-danger"
              onClick={() => void handleCleanupStuck()}
              disabled={cleaningUp}
              title="Delete sessions stuck in processing because the SDK never sent a stop signal"
            >
              <Trash2 size={14} />
              {cleaningUp ? 'Cleaning…' : `Clean up stuck (${stuckRecordings.length})`}
            </button>
          ) : null}
          <button
            type="button"
            className="secondary-button"
            onClick={() => void loadRecordings()}
            disabled={recordingsLoading}
            title="Refresh recordings list"
          >
            <RefreshCw size={14} className={recordingsLoading ? 'spin' : undefined} />
            Refresh
          </button>
          <button
            type="button"
            className="secondary-button"
            disabled={
              !canUseNotetakers ||
              !recall ||
              !sdkReady ||
              pendingStart ||
              Boolean(activeLiveSession)
            }
            onClick={() => void startAdhoc()}
            title={
              !canUseNotetakers
                ? 'Recording is restricted — ask your admin for the Notetaker permission.'
                : !recall
                  ? 'Desktop app required'
                  : 'Capture screen + system audio'
            }
          >
            <ScreenShare size={14} />
            {pendingStart ? 'Preparing…' : 'Start ad-hoc'}
          </button>
        </div>
      </header>

      {!recall ? (
        <article
          className="work-card"
          style={{
            display: 'flex',
            gap: 12,
            alignItems: 'flex-start',
            background: '#f4f4f5',
            border: '1px solid #f3dca0',
            marginBottom: 16,
          }}
        >
          <AlertTriangle size={16} color="#a96a0a" style={{ marginTop: 2, flexShrink: 0 }} />
          <div>
            <p className="nt-subheading" style={{ marginBottom: 2 }}>
              {compatibility?.headline || 'Detecting environment…'}
            </p>
            <p className="nt-body" style={{ color: 'var(--nt-text-muted)' }}>
              {compatibility?.detail}
            </p>
          </div>
        </article>
      ) : null}

      {cleanupNotice ? (
        <article
          className="work-card"
          style={{
            display: 'flex',
            gap: 12,
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            background:
              cleanupNotice.tone === 'success'
                ? '#e6f4ec'
                : cleanupNotice.tone === 'partial'
                  ? '#fef4e1'
                  : 'rgba(179, 38, 30, 0.06)',
            border:
              cleanupNotice.tone === 'success'
                ? '1px solid #a8d6bb'
                : cleanupNotice.tone === 'partial'
                  ? '1px solid #f3dca0'
                  : '1px solid rgba(179, 38, 30, 0.25)',
            marginBottom: 16,
          }}
        >
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', minWidth: 0 }}>
            {cleanupNotice.tone === 'success' ? (
              <CheckCircle2 size={16} color="var(--nt-success)" style={{ marginTop: 2, flexShrink: 0 }} />
            ) : (
              <AlertTriangle
                size={16}
                color={cleanupNotice.tone === 'partial' ? 'var(--nt-warn)' : 'var(--nt-danger)'}
                style={{ marginTop: 2, flexShrink: 0 }}
              />
            )}
            <div style={{ minWidth: 0 }}>
              <p className="nt-subheading" style={{ marginBottom: 2 }}>
                {cleanupNotice.headline}
              </p>
              <p className="nt-body" style={{ color: 'var(--nt-text-muted)' }}>
                {cleanupNotice.detail}
              </p>
            </div>
          </div>
          <button
            type="button"
            className="secondary-button is-icon"
            onClick={() => setCleanupNotice(null)}
            aria-label="Dismiss"
          >
            <X size={14} />
          </button>
        </article>
      ) : null}

      {activeLiveSession && canUseNotetakers ? (
        <LiveBanner
          session={activeLiveSession}
          onPause={() => void pauseRecording(activeLiveSession.windowId)}
          onResume={() => void resumeRecording(activeLiveSession.windowId)}
          onStop={() => void stopRecording(activeLiveSession.windowId)}
          liveTranscriptRef={liveTranscriptScrollRef}
        />
      ) : null}

      {recall && canUseNotetakers && liveSessions.some((s) => s.status === 'detected') ? (
        <DetectedMeetingsRow
          sessions={liveSessions.filter((s) => s.status === 'detected')}
          pending={pendingStart}
          onRecord={(s) => void startRecording(s.windowId, s.title, s.platform)}
        />
      ) : null}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(280px, 320px) minmax(0, 1fr)',
          gap: 16,
          marginTop: 16,
        }}
      >
        <RecordingsList
          groups={grouped}
          loading={recordingsLoading}
          error={recordingsError}
          selectedId={selectedId}
          filter={filter}
          onFilter={setFilter}
          onSelect={setSelectedId}
          nextCursor={nextCursor}
          onLoadMore={() => void loadRecordings(nextCursor || undefined)}
          totalCount={recordings.length}
          activeLiveId={activeLiveSession?.recordingId}
        />

        <RecordingDetail
          recording={selectedRecording}
          detailLoading={recordingDetailLoading}
          transcript={transcript}
          transcriptLoading={transcriptLoading}
          transcriptError={transcriptError}
          empty={!selectedRecording}
          tab={detailTab}
          onTabChange={setDetailTab}
          onDelete={handleDelete}
          canDelete={canUseNotetakers}
        />
      </div>

      <DiagnosticsPanel
        open={showLogs}
        onToggle={() => setShowLogs((v) => !v)}
        log={log}
        sdkState={sdkState}
        sdkReady={sdkReady}
        sdkError={sdkError}
      />
    </section>
  );
}

function LiveBanner({
  session,
  onPause,
  onResume,
  onStop,
  liveTranscriptRef,
}: {
  session: LiveSession;
  onPause: () => void;
  onResume: () => void;
  onStop: () => void;
  liveTranscriptRef: React.MutableRefObject<HTMLDivElement | null>;
}) {
  const isPaused = session.status === 'paused';
  return (
    <article className="nt-live-banner" style={{ marginBottom: 16 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', minWidth: 0 }}>
          <span aria-hidden="true" className={`nt-live-dot${isPaused ? ' is-paused' : ''}`} />
          <div style={{ minWidth: 0 }}>
            <p className="nt-section-label" style={{ marginBottom: 2 }}>
              {isPaused ? 'Paused' : 'Recording'}
            </p>
            <p className="nt-subheading" style={{ fontSize: 16 }}>
              {session.title || 'Active session'}
            </p>
            <p className="nt-meta" style={{ marginTop: 2 }}>
              {platformLabel(session.platform)} · started {new Date(session.detectedAt).toLocaleTimeString()}
            </p>
          </div>
        </div>
        <div style={{ display: 'inline-flex', gap: 6 }}>
          {isPaused ? (
            <button type="button" className="secondary-button" onClick={onResume}>
              <Play size={13} /> Resume
            </button>
          ) : (
            <button type="button" className="secondary-button" onClick={onPause}>
              <Pause size={13} /> Pause
            </button>
          )}
          <button type="button" className="secondary-button is-primary" onClick={onStop}>
            <Square size={13} /> Stop
          </button>
        </div>
      </div>
      <div
        ref={liveTranscriptRef}
        style={{
          maxHeight: 220,
          overflowY: 'auto',
          background: 'rgba(255,255,255,0.7)',
          border: '1px solid rgba(0,0,0,0.05)',
          borderRadius: 10,
          padding: 12,
          fontSize: 13,
          lineHeight: 1.6,
        }}
      >
        {session.liveTranscript.length === 0 ? (
          <span
            className="nt-meta"
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <Mic size={13} /> Waiting for the first words…
          </span>
        ) : (
          session.liveTranscript.map((line) => (
            <div key={line.id} style={{ marginBottom: 6, color: 'var(--nt-text-body)' }}>
              <strong style={{ color: 'var(--nt-text-strong)', fontWeight: 600 }}>{line.speaker}:</strong>{' '}
              <span>{line.text}</span>
            </div>
          ))
        )}
      </div>
    </article>
  );
}

function DetectedMeetingsRow({
  sessions,
  pending,
  onRecord,
}: {
  sessions: LiveSession[];
  pending: boolean;
  onRecord: (session: LiveSession) => void;
}) {
  return (
    <article className="work-card" style={{ padding: 14, marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <Video size={14} color="var(--nt-text-muted)" />
        <strong className="nt-subheading">
          {sessions.length} meeting{sessions.length === 1 ? '' : 's'} ready to record
        </strong>
      </div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {sessions.map((s) => (
          <div key={s.windowId} className="nt-detected">
            <CircleDot size={13} color="var(--nt-text-muted)" />
            <span style={{ minWidth: 0 }}>
              <strong style={{ fontWeight: 600 }}>{s.title || s.windowId}</strong>{' '}
              <span className="nt-caption">· {platformLabel(s.platform)}</span>
            </span>
            <button type="button" className="secondary-button" disabled={pending} onClick={() => onRecord(s)}>
              <Play size={12} /> Record
            </button>
          </div>
        ))}
      </div>
    </article>
  );
}

function RecordingsList({
  groups,
  loading,
  error,
  selectedId,
  filter,
  onFilter,
  onSelect,
  nextCursor,
  onLoadMore,
  totalCount,
  activeLiveId,
}: {
  groups: RecordingGroup[];
  loading: boolean;
  error: string | null;
  selectedId: string | null;
  filter: string;
  onFilter: (value: string) => void;
  onSelect: (id: string) => void;
  nextCursor: string | null;
  onLoadMore: () => void;
  totalCount: number;
  activeLiveId?: string;
}) {
  return (
    <article
      className="work-card"
      style={{
        padding: 0,
        display: 'flex',
        flexDirection: 'column',
        height: 'fit-content',
        maxHeight: '70vh',
        minHeight: 460,
        overflow: 'hidden',
      }}
    >
      <div style={{ padding: '14px 14px 12px', borderBottom: '1px solid var(--nt-border)' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            marginBottom: 10,
          }}
        >
          <strong className="nt-subheading">Sessions</strong>
          <span className="nt-caption">{totalCount}</span>
        </div>
        <div className="nt-search">
          <Search size={13} color="var(--nt-text-faint)" />
          <input value={filter} onChange={(e) => onFilter(e.target.value)} placeholder="Search title or id…" />
        </div>
      </div>

      <div style={{ overflowY: 'auto', flex: 1 }}>
        {loading && groups.length === 0 ? (
          <div
            className="nt-meta"
            style={{ padding: 24, display: 'flex', alignItems: 'center', gap: 8 }}
          >
            <Loader2 size={14} className="spin" /> Loading sessions…
          </div>
        ) : null}

        {error ? (
          <div className="nt-body" style={{ padding: 16, color: 'var(--nt-danger)' }}>
            {error}
          </div>
        ) : null}

        {!loading && !error && groups.length === 0 ? (
          <div className="nt-meta" style={{ padding: 24 }}>
            No notetaker sessions yet. When you record an interview, it will appear here.
          </div>
        ) : null}

        {groups.map((group) => (
          <div key={group.label}>
            <div className="nt-section-label" style={{ padding: '12px 14px 4px' }}>
              {group.label}
            </div>
            {group.recordings.map((rec) => {
              const isSelected = rec.id === selectedId;
              const isLive = activeLiveId === rec.id;
              const status = transcriptStatus(rec);
              return (
                <button
                  key={rec.id}
                  type="button"
                  className={`nt-session${isSelected ? ' is-selected' : ''}`}
                  onClick={() => onSelect(rec.id)}
                >
                  <div className="nt-session-title">
                    <strong>{recordingTitle(rec)}</strong>
                    {isLive ? (
                      <span className="nt-pill is-live">● live</span>
                    ) : (
                      <span className={pillClassFor(status)}>{status.replace(/_/g, ' ')}</span>
                    )}
                  </div>
                  <div className="nt-session-meta">
                    <span>{rec.created_at ? new Date(rec.created_at).toLocaleTimeString() : ''}</span>
                    <span>·</span>
                    <span>{platformLabel(recordingPlatform(rec))}</span>
                    {rec.completed_at || rec.started_at ? (
                      <>
                        <span>·</span>
                        <span>{formatDuration(rec.started_at, rec.completed_at)}</span>
                      </>
                    ) : null}
                  </div>
                </button>
              );
            })}
          </div>
        ))}

        {nextCursor ? (
          <button
            type="button"
            onClick={onLoadMore}
            disabled={loading}
            style={{
              width: '100%',
              padding: 12,
              border: 'none',
              borderTop: '1px solid var(--nt-border)',
              background: 'transparent',
              color: 'var(--nt-text-muted)',
              fontSize: 12,
              cursor: 'pointer',
            }}
          >
            {loading ? 'Loading…' : 'Load older sessions'}
          </button>
        ) : null}
      </div>
    </article>
  );
}

function RecordingDetail({
  recording,
  detailLoading,
  transcript,
  transcriptLoading,
  transcriptError,
  empty,
  tab,
  onTabChange,
  onDelete,
  canDelete,
}: {
  recording: RecallRecording | null;
  detailLoading: boolean;
  transcript: RecallTranscriptResponse | null;
  transcriptLoading: boolean;
  transcriptError: string | null;
  empty: boolean;
  tab: DetailTab;
  onTabChange: (tab: DetailTab) => void;
  onDelete: (id: string) => Promise<void> | void;
  canDelete: boolean;
}) {
  if (empty) {
    return (
      <article className="work-card" style={{ padding: 0 }}>
        <div className="nt-empty">
          <FileText size={26} />
          <strong>Pick a session to inspect</strong>
          <p>
            Sessions show up on the left as soon as they upload to Recall. Pick one to play the recording, read
            the transcript, see participants, or download the media.
          </p>
        </div>
      </article>
    );
  }
  if (!recording) return null;

  const transcriptText = useTranscriptText(transcript?.transcript || null);
  const titleText = recordingTitle(recording);
  const video = videoUrl(recording);
  const audio = audioUrl(recording);
  const videoStatus = videoStatusOf(recording);
  const audioStatus = audioStatusOf(recording);
  const overallStatus = transcriptStatus(recording);

  async function copyTranscript() {
    if (!transcriptText) return;
    try {
      await navigator.clipboard.writeText(transcriptText);
    } catch {
      // ignore
    }
  }

  function downloadTranscript() {
    if (!transcriptText) return;
    const blob = new Blob([transcriptText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = safeFilename(titleText, 'txt');
    a.click();
    URL.revokeObjectURL(url);
  }

  function downloadJson() {
    const blob = new Blob([JSON.stringify(recording, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = safeFilename(`${titleText}-meta`, 'json');
    a.click();
    URL.revokeObjectURL(url);
  }

  async function handleDelete() {
    const ok = window.confirm(
      `Permanently delete this Recall recording?\n\n${titleText}\n\nThe media and transcript on Recall.ai will be removed.`,
    );
    if (!ok) return;
    await onDelete(recording!.id);
  }

  return (
    <article
      className="work-card"
      style={{
        padding: 0,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 460,
        overflow: 'hidden',
      }}
    >
      <div style={{ padding: '16px 18px', borderBottom: '1px solid var(--nt-border)' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <div style={{ minWidth: 0, flex: 1 }}>
            <p className="nt-heading" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {titleText}
            </p>
            <div className="nt-meta-row" style={{ marginTop: 8 }}>
              <span>
                <Clock3 size={12} />
                {shortDateTime(recording.created_at)}
              </span>
              <span>
                <Video size={12} />
                {platformLabel(recordingPlatform(recording))}
              </span>
              <span>
                <Users size={12} />
                {recording.participants?.length ?? 0} participant
                {(recording.participants?.length ?? 0) === 1 ? '' : 's'}
              </span>
              {recording.started_at || recording.completed_at ? (
                <span>{formatDuration(recording.started_at, recording.completed_at)}</span>
              ) : null}
              <span className={pillClassFor(overallStatus)}>{overallStatus.replace(/_/g, ' ')}</span>
              {detailLoading ? (
                <span className="nt-caption" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <Loader2 size={11} className="spin" /> refreshing
                </span>
              ) : null}
            </div>
          </div>
          {canDelete ? (
            <button
              type="button"
              className="secondary-button is-icon is-danger"
              onClick={() => void handleDelete()}
              title="Delete this recording on Recall.ai"
              aria-label="Delete recording"
            >
              <Trash2 size={14} />
            </button>
          ) : null}
        </div>

        <div className="nt-action-row" style={{ marginTop: 14 }}>
          <div className="notetakers-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'transcript'}
              className={tab === 'transcript' ? 'active' : ''}
              onClick={() => onTabChange('transcript')}
            >
              <FileText size={13} /> Transcript
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'video'}
              className={tab === 'video' ? 'active' : ''}
              onClick={() => onTabChange('video')}
            >
              <Film size={13} /> Video
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'audio'}
              className={tab === 'audio' ? 'active' : ''}
              onClick={() => onTabChange('audio')}
            >
              <Headphones size={13} /> Audio
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'meta'}
              className={tab === 'meta' ? 'active' : ''}
              onClick={() => onTabChange('meta')}
            >
              <Info size={13} /> Details
            </button>
          </div>

          <div className="actions">
            {tab === 'transcript' ? (
              <>
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => void copyTranscript()}
                  disabled={!transcriptText}
                >
                  <Copy size={13} /> Copy
                </button>
                <button
                  type="button"
                  className="secondary-button"
                  onClick={downloadTranscript}
                  disabled={!transcriptText}
                >
                  <Download size={13} /> .txt
                </button>
              </>
            ) : null}
            {tab === 'video' && video ? (
              <button
                type="button"
                className="secondary-button"
                onClick={() => downloadUrl(video, safeFilename(titleText, 'mp4'))}
              >
                <Download size={13} /> .mp4
              </button>
            ) : null}
            {tab === 'audio' && audio ? (
              <button
                type="button"
                className="secondary-button"
                onClick={() => downloadUrl(audio, safeFilename(titleText, 'mp3'))}
              >
                <Download size={13} /> Audio
              </button>
            ) : null}
            {tab === 'meta' ? (
              <button type="button" className="secondary-button" onClick={downloadJson}>
                <Download size={13} /> .json
              </button>
            ) : null}
          </div>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 18px' }}>
        {tab === 'transcript' ? (
          <TranscriptTab transcript={transcript} loading={transcriptLoading} error={transcriptError} />
        ) : null}
        {tab === 'video' ? <VideoTab src={video} status={videoStatus} /> : null}
        {tab === 'audio' ? <AudioTab src={audio} status={audioStatus} /> : null}
        {tab === 'meta' ? <MetaTab recording={recording} /> : null}
      </div>
    </article>
  );
}

function TranscriptTab({
  transcript,
  loading,
  error,
}: {
  transcript: RecallTranscriptResponse | null;
  loading: boolean;
  error: string | null;
}) {
  if (loading) {
    return (
      <div className="nt-meta" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Loader2 size={13} className="spin" /> Loading transcript…
      </div>
    );
  }
  if (error) return <div className="nt-body" style={{ color: 'var(--nt-danger)' }}>{error}</div>;
  if (transcript && !transcript.ready) {
    return (
      <p className="nt-body" style={{ color: 'var(--nt-text-muted)' }}>
        Transcript is still <strong>{transcript.status || 'processing'}</strong>. Recall typically finishes
        transcription within a minute or two of the session ending. If a session is stuck for hours, the
        recording likely never received a clean stop signal — use Delete to clean it up.
      </p>
    );
  }
  if (transcript?.transcript && transcript.transcript.length > 0) {
    return <TranscriptRenderer segments={transcript.transcript} />;
  }
  return (
    <p className="nt-body" style={{ color: 'var(--nt-text-muted)' }}>
      No transcript content for this session.
    </p>
  );
}

function VideoTab({ src, status }: { src: string | null; status: string | null }) {
  if (!src) {
    return (
      <p className="nt-body" style={{ color: 'var(--nt-text-muted)' }}>
        {status && status !== 'done'
          ? `Video is still ${status.replace(/_/g, ' ')}. Refresh in a moment.`
          : 'No mixed video for this session. Some uploads (audio-only or aborted recordings) never produce a video file.'}
      </p>
    );
  }
  return (
    <div>
      <video
        controls
        preload="metadata"
        src={src}
        style={{ width: '100%', maxHeight: 480, borderRadius: 10, background: '#000' }}
      />
      <p className="nt-caption" style={{ marginTop: 8 }}>
        Streamed directly from Recall.ai. Use the <strong>.mp4</strong> button to download for offline review.
      </p>
    </div>
  );
}

function AudioTab({ src, status }: { src: string | null; status: string | null }) {
  if (!src) {
    return (
      <p className="nt-body" style={{ color: 'var(--nt-text-muted)' }}>
        {status && status !== 'done'
          ? `Audio is still ${status.replace(/_/g, ' ')}. Refresh in a moment.`
          : 'No mixed audio for this session. The video file already includes audio — use the Video tab to listen.'}
      </p>
    );
  }
  return (
    <div>
      <audio controls preload="metadata" src={src} style={{ width: '100%' }} />
      <p className="nt-caption" style={{ marginTop: 8 }}>
        Streamed directly from Recall.ai. Use the <strong>Audio</strong> button to download.
      </p>
    </div>
  );
}

function MetaTab({ recording }: { recording: RecallRecording }) {
  const sdkMeta = (recording.sdk_upload?.metadata || {}) as Record<string, unknown>;
  const recordingMeta = (recording.metadata || {}) as Record<string, unknown>;
  const shortcuts = recording.media_shortcuts || {};

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <MetaSection title="Identifiers">
        <MetaRow label="Recording ID" value={recording.id} mono />
        {recording.sdk_upload?.id ? <MetaRow label="SDK upload ID" value={recording.sdk_upload.id} mono /> : null}
        {recording.status?.code ? <MetaRow label="Recording status" value={recording.status.code} /> : null}
      </MetaSection>

      <MetaSection title="Timeline">
        <MetaRow
          label="Created"
          value={recording.created_at ? new Date(recording.created_at).toLocaleString() : '—'}
        />
        <MetaRow
          label="Started"
          value={recording.started_at ? new Date(recording.started_at).toLocaleString() : '—'}
        />
        <MetaRow
          label="Completed"
          value={recording.completed_at ? new Date(recording.completed_at).toLocaleString() : '—'}
        />
        {recording.expires_at ? (
          <MetaRow label="Expires" value={new Date(recording.expires_at).toLocaleString()} />
        ) : null}
      </MetaSection>

      {recording.participants && recording.participants.length > 0 ? (
        <MetaSection title={`Participants (${recording.participants.length})`}>
          {recording.participants.map((p, idx) => (
            <MetaRow
              key={`${p.id ?? idx}`}
              label={p.is_host ? 'Host' : 'Participant'}
              value={p.name || `id:${p.id ?? '—'}`}
            />
          ))}
        </MetaSection>
      ) : null}

      {Object.keys(sdkMeta).length > 0 ? (
        <MetaSection title="SDK upload metadata">
          {Object.entries(sdkMeta).map(([k, v]) => (
            <MetaRow key={k} label={k} value={typeof v === 'string' ? v : JSON.stringify(v)} />
          ))}
        </MetaSection>
      ) : null}

      {Object.keys(recordingMeta).length > 0 ? (
        <MetaSection title="Recording metadata">
          {Object.entries(recordingMeta).map(([k, v]) => (
            <MetaRow key={k} label={k} value={typeof v === 'string' ? v : JSON.stringify(v)} />
          ))}
        </MetaSection>
      ) : null}

      {Object.keys(shortcuts).length > 0 ? (
        <MetaSection title="Media artifacts">
          {Object.entries(shortcuts).map(([k, v]) => (
            <MetaRow
              key={k}
              label={k}
              value={`${v?.status?.code || '—'}${v?.data?.download_url ? ' · download available' : ''}`}
            />
          ))}
        </MetaSection>
      ) : null}
    </div>
  );
}

function MetaSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="nt-section-label" style={{ marginBottom: 6 }}>{title}</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>{children}</div>
    </div>
  );
}

function MetaRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 12, alignItems: 'baseline' }}>
      <span className="nt-meta">{label}</span>
      <span
        className={mono ? 'nt-body nt-mono' : 'nt-body'}
        style={{ wordBreak: 'break-word', fontSize: mono ? 12 : 13 }}
      >
        {value}
      </span>
    </div>
  );
}

function useTranscriptText(segments: RecallTranscriptSegment[] | null) {
  return useMemo(() => {
    if (!segments) return '';
    return segments
      .map((seg) => {
        const speaker = seg.participant?.name || 'Speaker';
        const text = seg.words.map((w) => w.text).join(' ').trim();
        return text ? `${speaker}: ${text}` : '';
      })
      .filter(Boolean)
      .join('\n');
  }, [segments]);
}

function TranscriptRenderer({ segments }: { segments: RecallTranscriptSegment[] }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {segments.map((seg, idx) => {
        const startSeconds = seg.words[0]?.start_timestamp?.relative;
        const text = seg.words.map((w) => w.text).join(' ').trim();
        if (!text) return null;
        const speaker = seg.participant?.name || 'Speaker';
        return (
          <div
            key={idx}
            style={{
              display: 'grid',
              gridTemplateColumns: '64px 120px 1fr',
              gap: 12,
              alignItems: 'baseline',
              fontSize: 13,
              lineHeight: 1.6,
            }}
          >
            <span className="nt-mono nt-caption">{formatTimestamp(startSeconds)}</span>
            <strong style={{ fontWeight: 600, color: 'var(--nt-text-strong)' }}>{speaker}</strong>
            <span style={{ color: 'var(--nt-text-body)' }}>{text}</span>
          </div>
        );
      })}
    </div>
  );
}

function DiagnosticsPanel({
  open,
  onToggle,
  log,
  sdkState,
  sdkReady,
  sdkError,
}: {
  open: boolean;
  onToggle: () => void;
  log: LogEntry[];
  sdkState: 'idle' | 'recording' | 'paused';
  sdkReady: boolean;
  sdkError: string | null;
}) {
  return (
    <article className="work-card" style={{ padding: 0, marginTop: 16 }}>
      <button type="button" className="nt-diagnostics-toggle" onClick={onToggle}>
        {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
        Diagnostics
        <small>internal · SDK {sdkError ? 'error' : sdkReady ? sdkState : 'initializing'}</small>
      </button>
      {open ? (
        <div style={{ borderTop: '1px solid var(--nt-border)', padding: 14 }}>
          <div className="nt-log">
            {log.length === 0 ? (
              <span className="ts">awaiting events…</span>
            ) : (
              log.map((entry) => (
                <div key={entry.id} className={`lvl-${entry.level}`}>
                  <span className="ts">{new Date(entry.ts).toLocaleTimeString()}</span>
                  {entry.message}
                </div>
              ))
            )}
          </div>
          {sdkError ? (
            <p className="nt-body" style={{ color: 'var(--nt-danger)', marginTop: 10 }}>
              SDK error: {sdkError}
            </p>
          ) : null}
          <p className="nt-caption" style={{ marginTop: 10 }}>
            Surface for engineers. Hidden by default.
          </p>
        </div>
      ) : null}
    </article>
  );
}
